import { psmGet, psmPost, psmPatch } from "../api/api";


function unwrapData(response) {
  return response && response.data !== undefined ? response.data : response;
}

/**
 * The real payload shape is a nested tree, not a flat list:
 *   { Services: { <Group>: { <Module>: { <SubModule>: [{ featureName, serviceId }] } } } }
 * e.g. Services.HRMS Team.CoreHR Management["Shifts Master Configuration"] = [
 *   { featureName: "Add", serviceId: 129 }, ...
 * ]
 *
 * Every leaf feature is one selectable/togglable service, keyed by serviceId.
 */
function getServicesRoot(raw) {
  return raw?.Services ?? raw?.services ?? raw ?? {};
}

/**
 * Top-level-only list — one entry per group (e.g. "HRMS Team", "PSM",
 * "HRMS ESS"), nothing about modules/sub-modules/features. This is what
 * the "Manage access" checklist shows and toggles; each entry carries
 * every underlying serviceId nested anywhere under that group so a single
 * toggle grants/revokes everything within it at once.
 */
function groupServiceTreeByTopLevel(raw) {
  const root = getServicesRoot(raw);
  const list = [];

  Object.entries(root || {}).forEach(([groupName, modules]) => {
    const serviceIds = [];
    Object.values(modules || {}).forEach((subModules) => {
      Object.values(subModules || {}).forEach((features) => {
        (features || []).forEach((f) => serviceIds.push(f.serviceId));
      });
    });
    if (!serviceIds.length) return;
    list.push({
      serviceId: groupName,
      serviceName: groupName,
      groupName,
      serviceIds,
      isEnabled: false,
    });
  });

  return list;
}

/** Tree shape AccessTree.jsx expects — used by the read-only Catalog tab. */
function buildServiceTree(raw) {
  const root = getServicesRoot(raw);

  return Object.entries(root || {}).map(([groupName, modules]) => ({
    id: groupName,
    name: groupName,
    modules: Object.entries(modules || {}).map(([moduleName, subModules]) => ({
      id: `${groupName}::${moduleName}`,
      name: moduleName,
      sub_modules: Object.entries(subModules || {}).map(([subModuleName, features]) => ({
        id: `${groupName}::${moduleName}::${subModuleName}`,
        name: subModuleName,
        permissions: (features || []).map((feature) => ({
          id: feature.serviceId,
          name: feature.featureName,
        })),
      })),
    })),
  }));
}

/**
 * GET /tenant-services/get-services — X-Service-Code: PSM02S
 * Full master catalog of every service on the platform. Used to populate
 * the "Manage access" dropdown/checklist and the Services & Modules
 * Catalog tab. Not tenant-specific — no isEnabled flag is meaningful here
 * on its own (always comes back false/undefined until merged with an
 * assignment list).
 */
export async function getServiceCatalog() {
  const response = await psmGet("/tenant-services/get-services", "PSM02S");
  return groupServiceTreeByTopLevel(unwrapData(response));
}

/**
 * Same catalog, kept in its native Group -> Module -> SubModule -> Feature
 * shape (mapped onto AccessTree's modules/sub_modules/permissions fields)
 * for the read-only Services & Modules Catalog tab.
 */
export async function getServiceCatalogTree() {
  const response = await psmGet("/tenant-services/get-services", "PSM02S");
  return buildServiceTree(unwrapData(response));
}

/**
 * GET /tenant-services/search?tenantId=... — X-Service-Code: PSM02S
 * Raw assignment rows that already exist for this tenant. Will be EMPTY
 * for a tenant that has never been assigned anything — that's expected,
 * not an error.
 */
async function getTenantAssignments(tenantId) {
  const response = await psmGet(`/tenant-services/search?tenantId=${tenantId}`, "PSM02S");
  const raw = unwrapData(response);
  const list = Array.isArray(raw) ? raw : raw?.content ?? raw?.assignments ?? [];
  return list.map((a) => ({
    serviceId: a.serviceId ?? a.id,
    isEnabled: a.isEnabled ?? a.isActive ?? a.selected ?? false,
  }));
}

/**
 * Combines the master catalog with this tenant's existing assignments so
 * the UI always has the full list of selectable services, correctly
 * pre-ticked for whatever is already enabled.
 *
 *   - New tenant, nothing assigned yet -> full catalog, everything unticked.
 *   - Existing tenant -> full catalog, previously-enabled ones pre-ticked.
 */
export async function getTenantServiceSelection(tenantId) {
  const [catalog, assignments] = await Promise.all([
    getServiceCatalog(),
    getTenantAssignments(tenantId),
  ]);

  const enabledIds = new Set(
    assignments.filter((a) => a.isEnabled).map((a) => String(a.serviceId))
  );

  return catalog.map((group) => ({
    ...group,
    isEnabled: group.serviceIds.every((id) => enabledIds.has(String(id))),
  }));
}

/**
 * Full nested tree (Group -> Module -> SubModule -> Feature) merged with
 * this tenant's existing assignments — backs the checkbox-driven "Manage
 * access" screen. Every node (group, module, sub-module, feature) carries
 * a `serviceIds` array listing every leaf feature id beneath it, so a
 * single checkbox toggle (e.g. on "PSM") can select/deselect everything
 * nested under it at once, while individual features underneath can still
 * be selected/deselected on their own.
 *
 * Returns { tree, selectedServiceIds } — selectedServiceIds is the flat
 * list of leaf feature ids currently enabled for this tenant, ready to
 * feed straight into AccessTree's `selectedServiceIds` prop.
 */
export async function getTenantServiceCatalogTree(tenantId) {
  const [response, assignments] = await Promise.all([
    psmGet("/tenant-services/get-services", "PSM02S"),
    getTenantAssignments(tenantId),
  ]);
  const root = getServicesRoot(unwrapData(response));
  const enabledIds = new Set(
    assignments.filter((a) => a.isEnabled).map((a) => String(a.serviceId))
  );

  const tree = Object.entries(root || {}).map(([groupName, modules]) => {
    const moduleNodes = Object.entries(modules || {}).map(([moduleName, subModules]) => {
      const subModuleNodes = Object.entries(subModules || {}).map(([subModuleName, features]) => {
        const permissions = (features || []).map((feature) => ({
          id: String(feature.serviceId),
          name: feature.featureName,
        }));
        return {
          id: `${groupName}::${moduleName}::${subModuleName}`,
          name: subModuleName,
          permissions,
          serviceIds: permissions.map((p) => p.id),
        };
      });
      return {
        id: `${groupName}::${moduleName}`,
        name: moduleName,
        sub_modules: subModuleNodes,
        serviceIds: subModuleNodes.flatMap((s) => s.serviceIds),
      };
    });
    return {
      id: groupName,
      name: groupName,
      modules: moduleNodes,
      serviceIds: moduleNodes.flatMap((m) => m.serviceIds),
    };
  });

  const selectedServiceIds = tree
    .flatMap((service) => service.serviceIds)
    .filter((id) => enabledIds.has(String(id)));

  return { tree, selectedServiceIds };
}

/**
 * Diff-and-save helper for the checkbox tree: compares the leaf feature
 * ids that were originally enabled against whatever is checked now, and
 * fires only the bulk-toggle calls actually needed (one for newly-enabled
 * ids, one for newly-disabled ids).
 */
export async function saveTenantServiceLeafSelection(tenantId, originalSelectedServiceIds, selectedServiceIds) {
  const before = new Set((originalSelectedServiceIds || []).map(String));
  const after = new Set((selectedServiceIds || []).map(String));

  const toEnable = Array.from(after).filter((id) => !before.has(id));
  const toDisable = Array.from(before).filter((id) => !after.has(id));

  const calls = [];
  if (toEnable.length) calls.push(bulkToggleTenantServices(tenantId, toEnable, true));
  if (toDisable.length) calls.push(bulkToggleTenantServices(tenantId, toDisable, false));
  await Promise.all(calls);
}

/**
 * Per-tenant summary used by the Tenant Registry list's "Access" column —
 * one entry per top-level group the tenant has *any* enabled service in,
 * with a count of how many modules under that group have at least one
 * enabled feature. Returns [] for a tenant with nothing assigned yet, so
 * the row correctly falls back to "Access not configured".
 */
export async function getTenantServiceGrants(tenantId) {
  const [response, assignments] = await Promise.all([
    psmGet("/tenant-services/get-services", "PSM02S"),
    getTenantAssignments(tenantId),
  ]);
  const root = getServicesRoot(unwrapData(response));
  const enabledIds = new Set(
    assignments.filter((a) => a.isEnabled).map((a) => String(a.serviceId))
  );

  const grants = [];
  Object.entries(root || {}).forEach(([groupName, modules]) => {
    let moduleCount = 0;
    Object.values(modules || {}).forEach((subModules) => {
      const groupHasEnabledFeature = Object.values(subModules || {}).some((features) =>
        (features || []).some((f) => enabledIds.has(String(f.serviceId)))
      );
      if (groupHasEnabledFeature) moduleCount += 1;
    });
    if (moduleCount > 0) {
      grants.push({ serviceName: groupName, moduleCount });
    }
  });
  return grants;
}

/**
 * GET /tenant-services/service/{serviceId} — X-Service-Code: PSM02S
 * Tenants that currently have this service enabled — backs the "filter
 * by service" control on the Tenant Registry dashboard.
 */
export async function getTenantsByService(serviceId) {
  const response = await psmGet(`/tenant-services/service/${serviceId}`, "PSM02S");
  const raw = unwrapData(response);
  return Array.isArray(raw) ? raw : raw?.content ?? [];
}

/** POST /tenant-services/add — X-Service-Code: PSM02A (grants a service to a tenant) */
export async function addTenantService(tenantId, serviceId) {
  const response = await psmPost(
    "/tenant-services/add",
    { tenantId, serviceId, isEnabled: true },
    "PSM02A"
  );
  return unwrapData(response);
}

/** PATCH /tenant-services/update/toggle — X-Service-Code: PSM02U (toggles a single service on/off for a tenant) */


export async function updateTenantService(tenantId, serviceId, isEnabled) {
  const requestBody = { isEnabled };

  const response = await psmPatch(
    `/tenant-services/update/toggle?serviceIds=${serviceId}&tenantId=${tenantId}`,
    requestBody,
    "PSM02U"
  );
  return unwrapData(response);
}

/** PATCH /tenant-services/update/bulk-toggle — X-Service-Code: PSM02U (toggles multiple services at once for a tenant) */
export async function bulkToggleTenantServices(tenantId, serviceIds, isEnabled) {
  const serviceIdsParam = Array.isArray(serviceIds) ? serviceIds.join(",") : serviceIds;

  const requestBody = { isEnabled };

  const response = await psmPatch(
    `/tenant-services/update/toggle?serviceIds=${serviceIdsParam}&tenantId=${tenantId}`,
    requestBody,
    "PSM02U"
  );
  return unwrapData(response);
}


/**
 * Diff-and-save helper: given the original catalog (with isEnabled as loaded)
 * and the set of serviceIds currently checked in the UI, fires only the
 * add/update calls actually needed.
 */
export async function saveTenantServiceSelection(tenantId, originalServices, selectedServiceIds) {
  const selected = new Set(selectedServiceIds.map(String));

  const calls = originalServices.map((group) => {
    const id = String(group.serviceId);
    const wasEnabled = !!group.isEnabled;
    const isNowSelected = selected.has(id);

    if (isNowSelected && !wasEnabled) {
      // If your backend has a separate 'add' endpoint, you can keep addTenantService here.
      // Otherwise, bulkToggleTenantServices will work perfectly to enable them.
      return bulkToggleTenantServices(tenantId, group.serviceIds, true);
    }
    if (!isNowSelected && wasEnabled) {
      return bulkToggleTenantServices(tenantId, group.serviceIds, false);
    }
    return null;
  });

  await Promise.all(calls.filter(Boolean));
}
